import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Shield, UserPlus, Trash2, Crown, 
  Mail, Calendar, Search, Loader2, AlertTriangle,
  Eye, MessageSquare, Heart, Settings, ChevronDown, Share2
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { toast } from "sonner";
import AdminLayout from "@/components/AdminLayout";

const ADMIN_ROLES = [
  {
    id: "super_admin",
    label: "Super Admin",
    icon: Crown,
    color: "bg-amber-500",
    description: "Unrestricted access + team management",
    permissions: ["all"]
  },
  {
    id: "admin",
    label: "Admin",
    icon: Shield,
    color: "bg-indigo-500",
    description: "Members, moderation, support, comms, matchmaking, finance view (no finance changes, no team/security)",
    permissions: ["members", "moderation", "support", "comms", "analytics"]
  },
  {
    id: "moderator",
    label: "Moderator",
    icon: Eye,
    color: "bg-purple-500",
    description: "Reports, community & verification; can suspend reported members only",
    permissions: ["moderation", "verification", "analytics"]
  },
  {
    id: "support",
    label: "Support",
    icon: MessageSquare,
    color: "bg-blue-500",
    description: "Support tickets; member info only for ticket-linked members",
    permissions: ["support", "analytics"]
  },
  {
    id: "analyst",
    label: "Analyst",
    icon: Eye,
    color: "bg-green-500",
    description: "Analytics, finance aggregates and the audit log — read only, no member PII",
    permissions: ["analytics", "finance-read", "audit"]
  },
  {
    id: "marketing",
    label: "Marketing",
    icon: MessageSquare,
    color: "bg-pink-500",
    description: "Email campaigns & tester invites (recipient counts only — no member lists, no finance)",
    permissions: ["comms", "analytics"]
  },
  {
    id: "matchmaker",
    label: "Matchmaker",
    icon: Crown,
    color: "bg-rose-500",
    description: "Matchmaking tools and analytics only — no member lists or writes",
    permissions: ["matchmaking", "analytics"]
  }
];

const AdminTeam = () => {
  const navigate = useNavigate();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchEmail, setSearchEmail] = useState("");
  const [searchName, setSearchName] = useState("");
  const [selectedRole, setSelectedRole] = useState("support");
  const [adding, setAdding] = useState(false);
  const [showRoleDropdown, setShowRoleDropdown] = useState(false);
  const [editingRole, setEditingRole] = useState(null);

  useEffect(() => {
    fetchAdmins();
  }, []);

  const fetchAdmins = async () => {
    setLoading(true);
    try {
      const response = await api.get("/admin/team");
      setAdmins(response.data.admins || []);
    } catch (error) {
      console.error("Failed to fetch admins:", error);
      toast.error("Failed to load admin team");
    } finally {
      setLoading(false);
    }
  };

  const addAdmin = async () => {
    if (!searchEmail.trim()) {
      toast.error("Please enter an email address");
      return;
    }
    
    setAdding(true);
    try {
      const response = await api.post("/admin/team/add", { 
        email: searchEmail,
        role: selectedRole,
        name: searchName || ""
      });
      if (response.data.success) {
        // SEC-007 fix: no password — show the single-use setup link instead
        const setupLink = `${window.location.origin}${response.data.setup_path}`;
        const userName = response.data.user?.name || searchEmail;
        try { await navigator.clipboard.writeText(setupLink); } catch {}
        alert(
          `✅ ${userName} added as ${getRoleLabel(selectedRole)}!\n\n` +
          `🔗 Single-use setup link (copied to clipboard):\n${setupLink}\n\n` +
          `⚠️ Share this link with ${userName} only. It expires in 72 hours and can be used once. ` +
          `You can re-issue it from this page if needed.`
        );
        
        toast.success("Admin created — setup link copied to clipboard");
        setSearchEmail("");
        setSearchName("");
        fetchAdmins();
      } else {
        toast.error(response.data.error || "Failed to add admin");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add admin");
    } finally {
      setAdding(false);
    }
  };

  const updateRole = async (userId, newRole) => {
    try {
      const response = await api.post("/admin/team/update-role", { 
        user_id: userId,
        role: newRole
      });
      if (response.data.success) {
        toast.success("Role updated successfully");
        fetchAdmins();
      } else {
        toast.error(response.data.error || "Failed to update role");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update role");
    } finally {
      setEditingRole(null);
    }
  };

  const removeAdmin = async (userId, userName) => {
    if (!window.confirm(`Remove ${userName} from admin team?`)) return;
    
    try {
      const response = await api.post("/admin/team/remove", { user_id: userId });
      if (response.data.success) {
        toast.success(`${userName} removed from admin team`);
        fetchAdmins();
      } else {
        toast.error(response.data.error || "Failed to remove admin");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to remove admin");
    }
  };

  const getRoleLabel = (roleId) => {
    return ADMIN_ROLES.find(r => r.id === roleId)?.label || roleId;
  };

  const getRoleColor = (roleId) => {
    return ADMIN_ROLES.find(r => r.id === roleId)?.color || "bg-gray-500";
  };

  const getRoleIcon = (roleId) => {
    const role = ADMIN_ROLES.find(r => r.id === roleId);
    return role?.icon || Shield;
  };

  return (
    <AdminLayout>
    <div className="bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#1C3A7A] to-[#2a4a8a] text-white">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex items-center gap-4 mb-4">
            <div className="flex items-center gap-3">
              <div className="bg-white/20 p-2 rounded-lg">
                <Shield className="w-6 h-6" />
              </div>
              <div>
                <h1 className="text-2xl font-bold">Admin Team</h1>
                <p className="text-white/70 text-sm">Manage administrators and roles</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Role Legend */}
        <div className="bg-white rounded-2xl shadow-sm border p-6">
          <h2 className="text-lg font-semibold mb-4">Available Roles</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
            {ADMIN_ROLES.map((role) => {
              const IconComponent = role.icon;
              return (
                <div key={role.id} className="flex items-start gap-3 p-3 bg-gray-50 rounded-xl">
                  <div className={`${role.color} p-2 rounded-lg text-white`}>
                    <IconComponent className="w-4 h-4" />
                  </div>
                  <div>
                    <p className="font-medium text-sm">{role.label}</p>
                    <p className="text-xs text-gray-500">{role.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Add Admin */}
        <div className="bg-white rounded-2xl shadow-sm border p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <UserPlus className="w-5 h-5 text-[#1C3A7A]" />
            Add New Admin
          </h2>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  type="email"
                  placeholder="Email address *"
                  value={searchEmail}
                  onChange={(e) => setSearchEmail(e.target.value)}
                  className="pl-11"
                  onKeyDown={(e) => e.key === "Enter" && addAdmin()}
                />
              </div>
              <Input
                type="text"
                placeholder="Name (optional)"
                value={searchName}
                onChange={(e) => setSearchName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addAdmin()}
              />
            </div>
            
            {/* Role Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Select Role</label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {ADMIN_ROLES.filter(r => r.id !== "super_admin").map((role) => {
                  const IconComponent = role.icon;
                  return (
                    <button
                      key={role.id}
                      onClick={() => setSelectedRole(role.id)}
                      className={`flex items-center gap-2 p-3 rounded-xl border-2 transition-all ${
                        selectedRole === role.id 
                          ? "border-[#1C3A7A] bg-blue-50" 
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                    >
                      <div className={`${role.color} p-1.5 rounded-lg text-white`}>
                        <IconComponent className="w-3 h-3" />
                      </div>
                      <span className="text-sm font-medium">{role.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <Button 
              onClick={addAdmin} 
              disabled={adding || !searchEmail.trim()}
              className="w-full bg-[#1C3A7A] hover:bg-[#15305f]"
            >
              {adding ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <UserPlus className="w-4 h-4 mr-2" />}
              Add as {getRoleLabel(selectedRole)}
            </Button>
            
            <p className="text-xs text-gray-500 text-center">
              A single-use setup link (valid 72 hours) will be generated for you to share with the new admin.
            </p>
          </div>
          <p className="text-sm text-gray-500 mt-3">
            The new admin does not need an existing member account — they set their own password via the setup link.
          </p>
        </div>

        {/* Current Admins */}
        <div className="bg-white rounded-2xl shadow-sm border p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Crown className="w-5 h-5 text-[#FAB710]" />
            Current Admins ({admins.length})
          </h2>
          
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-[#1C3A7A]" />
            </div>
          ) : admins.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No admins found
            </div>
          ) : (
            <div className="space-y-3">
              {admins.map((admin) => {
                const RoleIcon = getRoleIcon(admin.role || "support");
                return (
                  <div 
                    key={admin.id} 
                    className="flex flex-wrap items-center justify-between gap-3 p-4 bg-gray-50 rounded-xl"
                  >
                    <div className="flex items-center gap-4 min-w-0">
                      <img
                        src={admin.photos?.[0] || `https://ui-avatars.com/api/?name=${encodeURIComponent(admin.name || 'A')}&background=1C3A7A&color=fff`}
                        alt={admin.name}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{admin.name}</span>
                        </div>
                        <span className="text-sm text-gray-500">{admin.email}</span>
                        
                        {/* Role Badge / Selector */}
                        <div className="mt-1">
                          {admin.is_super_admin ? (
                            <span className="inline-flex items-center gap-1 bg-amber-500 text-white text-xs px-2 py-0.5 rounded-full">
                              <Crown className="w-3 h-3" />
                              Super Admin
                            </span>
                          ) : editingRole === admin.id ? (
                            <div className="flex flex-wrap gap-1 mt-1">
                              {ADMIN_ROLES.filter(r => r.id !== "super_admin").map((role) => (
                                <button
                                  key={role.id}
                                  onClick={() => updateRole(admin.id, role.id)}
                                  className={`text-xs px-2 py-1 rounded-full ${
                                    admin.role === role.id 
                                      ? `${role.color} text-white` 
                                      : "bg-gray-200 hover:bg-gray-300"
                                  }`}
                                >
                                  {role.label}
                                </button>
                              ))}
                              <button
                                onClick={() => setEditingRole(null)}
                                className="text-xs px-2 py-1 text-gray-500"
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => setEditingRole(admin.id)}
                              className={`inline-flex items-center gap-1 ${getRoleColor(admin.role || "support")} text-white text-xs px-2 py-0.5 rounded-full hover:opacity-80`}
                            >
                              <RoleIcon className="w-3 h-3" />
                              {getRoleLabel(admin.role || "support")}
                              <ChevronDown className="w-3 h-3" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {!admin.is_super_admin && (
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          data-testid="reissue-setup-link-btn"
                          onClick={async () => {
                            try {
                              const r = await api.post("/admin/team/reissue-setup-link", { admin_id: admin.id });
                              if (r.data.success) {
                                const link = `${window.location.origin}${r.data.setup_path}`;
                                try { await navigator.clipboard.writeText(link); } catch {}
                                alert(`🔗 New single-use setup link (copied):\n${link}\n\nPrevious link and sessions revoked. Expires in 72 hours.`);
                              } else {
                                toast.error(r.data.error || "Failed to re-issue link");
                              }
                            } catch (e) {
                              toast.error(e.response?.data?.detail || "Failed to re-issue link");
                            }
                          }}
                          className="text-[#1C3A7A]"
                          title="Re-issue setup link (revokes previous link & sessions)"
                        >
                          🔗
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => removeAdmin(admin.id, admin.name)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Warning */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <div className="flex gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-medium text-amber-800">Important</h4>
              <p className="text-sm text-amber-700 mt-1">
                Admins have access based on their assigned role. Only Super Admins can add or remove team members.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
    </AdminLayout>
  );
};

export default AdminTeam;
